import Input from "../../ui/Input";
import {useAlert} from "react-alert";
import Loading from "../../ui/Loading";
import {useState} from "react";
import {makePayment} from "../../Services";
import Select from "../../ui/Input/Select";
import PageTitle from "../../ui/PageTitle";
import STATES from "../../constants/states";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import MESSAGES from "../../constants/messages";
import CreditCardInput from "react-credit-card-input";
import SignatureModal from "../../ui/Signature/SignatureModal";
import StepperFooter from "../shared/Stepper/components/StepperFooter";

import "./index.scss";

import jsb from "../../scss/img/jcb.png";
import visa from "../../scss/img/visa.png";
import master from "../../scss/img/masrercard.png";
import discover from "../../scss/img/discover-logo.png";
import express from "../../scss/img/american-express.png";
import {formatNumberToShow} from "../../utils";

// Extra, independent kill switch on top of Hub's own PAYMENTS_ENABLED gate
// (which normally stops a customer from ever reaching this page) -- this one
// really does charge a real card on the live account, so it gets its own
// direct check rather than relying only on upstream gating.
const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED === "true";

// Standalone "just pay" page for a registration that was already created
// (address/carrier/registration steps done) via MyRegistrations' native UCR
// form -- reuses the exact same makePayment(id, data) call and card UI as
// the full wizard's own PaymentInfo step, just without the preceding steps'
// client state, since this record didn't come from an in-page wizard session.
const PayOnly = () => {
  const alert = useAlert();
  const navigate = useNavigate();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name") || "";
  const total = searchParams.get("total") || "";

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    zip: "",
    city: "",
    address: "",
    cardCode: "",
    lastName: "",
    firstName: "",
    cardNumber: "",
    expirationDate: "",
    state: STATES[0].state,
  });

  const onChange = (e, key) => {
    setData(prev => ({
      ...prev,
      [key]: e.target.value
    }));
  };

  if (!PAYMENTS_ENABLED) {
    return (
      <div className="pay-only container">
        <PageTitle title={MESSAGES.PAY_TO_COMPLETE_REGISTRATION} />
        <p>Payments are temporarily paused while we finish testing. Please check back soon — your registration is saved.</p>
      </div>
    );
  }

  const onClickNext = () => {
    setOpen(true);
  };

  const onSave = (signature) => {
    setOpen(false);
    setLoading(true);

    const arr = data.expirationDate.split("/");
    const d = {
      signature,
      zip: data.zip,
      city: data.city,
      state: data.state,
      address: data.address,
      cardCode: data.cardCode,
      lastName: data.lastName,
      firstName: data.firstName,
      cardNumber: data.cardNumber.replaceAll(" ", ""),
      expirationDate: arr.length === 2 ? `20${arr[1].trim()}-${arr[0].trim()}` : data.expirationDate,
    };

    makePayment(id, d)
      .then(response => {
        if (response.hasError) {
          response.errors.map(({ errorMessage }) => alert.show(errorMessage, {type: "error"}));
        } else {
          // total comes from the same query param already shown as "Total due"
          // above -- the real amount charged, not a placeholder.
          navigate("/success-page", {
            state: {
              transactionId: `UCR-${id}`,
              value: total ? Number(total) : undefined,
            },
          });
        }
      })
      .catch(err => {
        alert.show(err.message || err, {type: "error"});
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="pay-only container">
      <PageTitle title={MESSAGES.PAY_TO_COMPLETE_REGISTRATION} />
      {(name || total) && (
        <div className="order-details">
          {name && <div className="total-row"><label>Business</label><span>{name}</span></div>}
          {total && <div className="total-row total-cost"><label>Total due</label><span>${formatNumberToShow(total)}.00</span></div>}
        </div>
      )}
      <div className="payment-logos">
        {MESSAGES.PAY_WITH_CREDIT_CARD}
        <div><img src={visa} alt="visa" /></div>
        <div><img src={master} alt="master" /></div>
        <div><img src={discover} alt="discover" /></div>
        <div><img src={jsb} alt="jsb" /></div>
        <div><img src={express} alt="american express" /></div>
      </div>
      <CreditCardInput
        cardNumberInputProps={{
          value: data.cardNumber,
          onChange: e => onChange(e, "cardNumber")
        }}
        cardExpiryInputProps={{ value: data.expirationDate, onChange: e => onChange(e, "expirationDate") }}
        cardCVCInputProps={{ value: data.cardCode, onChange: e => onChange(e, "cardCode") }}
        fieldClassName="input"
      />
      <div className="name-info">
        <Input
          required
          label="First Name"
          value={data.firstName}
          onChange={e => onChange(e, "firstName")}
        />
        <Input
          required
          label="Last Name"
          value={data.lastName}
          onChange={e => onChange(e, "lastName")}
        />
      </div>
      <p className="group-input-label">{MESSAGES.BILLING_ADDRESS}</p>
      <div className="address-row">
        <Input
          required
          value={data.address}
          label={MESSAGES.ADDRESS}
          placeholder={MESSAGES.ADDRESS}
          onChange={e => onChange(e, "address")}
        />
        <Input
          required
          value={data.city}
          label={MESSAGES.CITY}
          placeholder={MESSAGES.CITY}
          onChange={e => onChange(e, "city")}
        />
        <Select
          required
          value={data.state}
          label={MESSAGES.STATE}
          onChange={e => onChange(e, "state")}
        >
          {STATES.map(m => (
            <option value={m.state} key={m.name}>{m.state} {m.name}</option>
          ))}
        </Select>
        <Input
          required
          value={data.zip}
          label={MESSAGES.ZIP_CODE}
          placeholder={MESSAGES.ZIP_CODE}
          onChange={e => onChange(e, "zip")}
        />
      </div>
      {open && (
        <SignatureModal
          open={open}
          onSave={onSave}
          setOpen={setOpen}
        />
      )}
      <StepperFooter
        successText="Pay"
        classNames="danger"
        onClickNext={onClickNext}
      />
      <Loading isLoading={loading} />
    </div>
  );
};

export default PayOnly;
